import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "components/Button";
import { ROUTES } from "shared/constants/routes";
import { fetchCart } from "actions/dtfCart";
import { prepareCartItemForApi } from "pages/dtf-transfers/by-size/helpers";
import {
  MIN_ORDER_SUBTOTAL,
  getRushSameDayFee,
  meetsMinOrderSubtotal,
  readRushSameDayPreference,
  writeRushSameDayPreference,
} from "pages/dtf-transfers/by-size/data";
import COLORS from "shared/constants/colors";
import CheckoutSteps from "pages/dtf-transfers/checkout/CheckoutSteps";
import ContinueShoppingButton from "pages/dtf-transfers/checkout/ContinueShoppingButton";
import CheckoutSummary from "pages/dtf-transfers/checkout/CheckoutSummary";
import InformationStep from "pages/dtf-transfers/checkout/InformationStep";
import ShippingStep, {
  SHIPPING_OPTIONS,
} from "pages/dtf-transfers/checkout/ShippingStep";
import PaymentStep from "pages/dtf-transfers/checkout/PaymentStep";
import {
  formatMoney,
  getCurrencyForCountry,
  getCountryForCurrency,
} from "pages/dtf-transfers/checkout/currency";
import {
  Page,
  MainColumn,
  SummaryColumn,
  FooterActions,
  FooterActionGroup,
  ContinueButtonWrap,
  BackLink,
  EmptyCheckout,
  EmptyCheckoutText,
} from "styles/pages/dtf-transfers/checkout";

const CHECKOUT_TAX_RATE = 0.13;

const INITIAL_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  addressLineOne: "",
  addressLineTwo: "",
  city: "",
  stateProvince: "",
  country: "CA",
  zipPostal: "",
  phone: "",
};

const REQUIRED_INFORMATION_FIELDS = [
  "email",
  "firstName",
  "lastName",
  "addressLineOne",
  "city",
  "stateProvince",
  "country",
  "zipPostal",
];

const getInformationFieldErrors = (formData) =>
  REQUIRED_INFORMATION_FIELDS.reduce((errors, field) => {
    if (!formData[field]?.trim()) {
      errors[field] = true;
    }
    return errors;
  }, {});

const isInformationValid = (formData) =>
  Object.keys(getInformationFieldErrors(formData)).length === 0;

const DtfCheckout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state?.dtfCart?.items || []);
  const isCartLoading = useSelector((state) => state?.dtfCart?.loading);
  const emailid = useSelector((state) => state?.auth?.profile?.emailid);
  const token = useSelector((state) => state?.auth?.profile?.token);
  const reduxCurrency = useSelector(
    (state) => state?.currency?.value || "CAD",
  );

  const [currentStep, setCurrentStep] = useState("information");
  const [formData, setFormData] = useState(() => ({
    ...INITIAL_FORM,
    country: getCountryForCurrency(reduxCurrency),
  }));
  const [selectedShipping, setSelectedShipping] = useState("standard");
  const [rushSameDay, setRushSameDay] = useState(readRushSameDayPreference);

  const handleRushSameDayChange = useCallback((checked) => {
    setRushSameDay(checked);
    writeRushSameDayPreference(checked);
  }, []);

  useEffect(() => {
    const country = getCountryForCurrency(reduxCurrency);
    setFormData((prev) => {
      if (prev.country === country) return prev;
      return { ...prev, country, stateProvince: "" };
    });
  }, [reduxCurrency]);

  const currency = getCurrencyForCountry(formData.country);
  const itemsSubtotal = cartItems.reduce(
    (sum, item) => sum + (item.total || 0),
    0,
  );
  const rushSameDayFee = getRushSameDayFee(itemsSubtotal, rushSameDay);
  const subtotal = Number((itemsSubtotal + rushSameDayFee).toFixed(2));
  const meetsMinimum = meetsMinOrderSubtotal(itemsSubtotal, rushSameDay);
  const shippingCost =
    SHIPPING_OPTIONS.find((option) => option.id === selectedShipping)?.price ||
    0;
  const showTax = currentStep !== "information";
  const shippingForTotal = showTax ? shippingCost : 0;
  const taxAmount = showTax
    ? Number(((subtotal + shippingForTotal) * CHECKOUT_TAX_RATE).toFixed(2))
    : 0;
  const total = subtotal + shippingForTotal + taxAmount;

  const checkoutData = useMemo(
    () => ({
      contact: {
        email: formData.email,
      },
      shippingAddress: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.company,
        addressLineOne: formData.addressLineOne,
        addressLineTwo: formData.addressLineTwo,
        city: formData.city,
        stateProvince: formData.stateProvince,
        country: formData.country,
        zipPostal: formData.zipPostal,
        phone: formData.phone,
      },
      shippingMethod: selectedShipping,
      rushSameDay,
      rushSameDayFee,
      itemsSubtotal,
      items: cartItems.map((item) =>
        prepareCartItemForApi({
          ...item,
          removeBackground: Boolean(item.removeBackground),
          cutSeparately: Boolean(item.cutSeparately),
          cuttingUnitPrice: item.cuttingUnitPrice ?? 0,
          cuttingFee: item.cuttingFee ?? 0,
          artworkCheck: Boolean(item.artworkCheck),
          artworkCheckFee: item.artworkCheckFee ?? 0,
        }),
      ),
      subtotal,
      shippingCost: shippingForTotal,
      taxRate: CHECKOUT_TAX_RATE,
      taxAmount,
      total,
      currency,
    }),
    [
      formData,
      selectedShipping,
      rushSameDay,
      rushSameDayFee,
      itemsSubtotal,
      cartItems,
      subtotal,
      shippingForTotal,
      taxAmount,
      total,
      currency,
    ],
  );

  useEffect(() => {
    if (emailid) {
      dispatch(fetchCart(emailid, token));
    }
  }, [dispatch, emailid, token]);

  useEffect(() => {
    if (!isCartLoading && !cartItems.length) {
      navigate(ROUTES.DTF_TRANSFERS_CART.LINK, { replace: true });
    }
  }, [cartItems.length, isCartLoading, navigate]);

  const handleFormChange = useCallback((updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleStepNavigation = (target) => {
    if (typeof target === "string" && target.startsWith("/")) {
      navigate(target);
      return;
    }

    setCurrentStep(target);
  };

  const informationFieldErrors = getInformationFieldErrors(formData);
  const isInformationStepValid = isInformationValid(formData);
  const isContinueDisabled =
    (currentStep === "information" && !isInformationStepValid) ||
    (currentStep === "shipping" && !meetsMinimum);

  const handleContinue = () => {
    if (currentStep === "information") {
      if (!isInformationStepValid) return;

      setCurrentStep("shipping");
      return;
    }

    if (currentStep === "shipping") {
      if (!meetsMinimum) {
        window.alert(
          `A minimum subtotal of ${formatMoney(MIN_ORDER_SUBTOTAL, currency)} is required to place an order.`,
        );
        return;
      }

      setCurrentStep("payment");
    }
  };

  const handleBack = () => {
    if (currentStep === "payment") {
      setCurrentStep("shipping");
      return;
    }

    if (currentStep === "shipping") {
      setCurrentStep("information");
      return;
    }

    navigate(ROUTES.DTF_TRANSFERS_CART.LINK);
  };

  const continueLabel =
    currentStep === "information"
      ? "Continue to shipping"
      : currentStep === "shipping"
        ? "Continue to payment"
        : null;

  if (isCartLoading) {
    return (
      <EmptyCheckout>
        <EmptyCheckoutText>Loading checkout...</EmptyCheckoutText>
      </EmptyCheckout>
    );
  }

  if (!cartItems.length) {
    return (
      <EmptyCheckout>
        <EmptyCheckoutText>Your cart is empty.</EmptyCheckoutText>
        <Button
          title="Return to cart"
          onClick={() => navigate(ROUTES.DTF_TRANSFERS_CART.LINK)}
          containerStyle={{
            backgroundColor: COLORS.BLUE,
            color: COLORS.WHITE,
            textTransform: "none",
            borderRadius: "6px",
            padding: "10px 24px",
            fontSize: "15px",
          }}
        />
      </EmptyCheckout>
    );
  }

  return (
    <Page>
      <MainColumn>
        <CheckoutSteps
          currentStep={currentStep}
          onStepClick={handleStepNavigation}
        />

        {currentStep === "information" ? (
          <InformationStep
            formData={formData}
            fieldErrors={informationFieldErrors}
            onChange={handleFormChange}
          />
        ) : null}

        {currentStep === "shipping" ? (
          <ShippingStep
            selectedShipping={selectedShipping}
            onChange={setSelectedShipping}
            currency={currency}
          />
        ) : null}

        {currentStep === "payment" ? (
          <PaymentStep
            total={total}
            checkoutData={checkoutData}
            currency={currency}
          />
        ) : null}

        {currentStep !== "payment" ? (
          <FooterActions>
            <BackLink type="button" onClick={handleBack}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              {currentStep === "information"
                ? "Return to cart"
                : "Return to previous step"}
            </BackLink>

            <FooterActionGroup>
              <ContinueShoppingButton showDivider={false} />
              <ContinueButtonWrap>
                <Button
                  title={continueLabel}
                  endIcon={<ArrowForwardIcon />}
                  disabled={isContinueDisabled}
                  onClick={handleContinue}
                  containerStyle={{
                    backgroundColor: isContinueDisabled
                      ? "#9ca3af"
                      : COLORS.BLUE,
                    color: COLORS.WHITE,
                    textTransform: "none",
                    borderRadius: "6px",
                    padding: "12px 24px",
                    fontSize: "15px",
                  }}
                />
              </ContinueButtonWrap>
            </FooterActionGroup>
          </FooterActions>
        ) : (
          <FooterActions>
            <BackLink type="button" onClick={handleBack}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Return to shipping
            </BackLink>

            <FooterActionGroup>
              <ContinueShoppingButton showDivider={false} />
            </FooterActionGroup>
          </FooterActions>
        )}
      </MainColumn>

      <SummaryColumn>
        <CheckoutSummary
          cartItems={cartItems}
          subtotal={subtotal}
          itemsSubtotal={itemsSubtotal}
          rushSameDay={rushSameDay}
          onRushSameDayChange={handleRushSameDayChange}
          shippingCost={showTax ? shippingCost : 0}
          taxAmount={taxAmount}
          showTax={showTax}
          currency={currency}
        />
      </SummaryColumn>
    </Page>
  );
};

export default DtfCheckout;
